import {
  OrganizationRegulation,
  ComplianceRequirement,
  ComplianceRequirementAssessment,
  ComplianceRequirementMapping,
  ComplianceRequirementSourceLink,
  RegulatoryChangeRecord,
  RegulatoryChangeAction,
  RegulationsDashboardMetrics,
} from './types';

const API_BASE = '/api/compliance/regulations';
const DEFAULT_ORG = 'ORG-XYZ';

function getHeaders() {
  return {
    'Content-Type': 'application/json',
    'x-organization-id': DEFAULT_ORG,
  };
}

export const regulationsService = {
  // Metrics
  async getMetrics(): Promise<RegulationsDashboardMetrics> {
    const res = await fetch(`${API_BASE}/metrics`, { headers: getHeaders() });
    if (!res.ok) throw new Error('Failed to load compliance metrics');
    return res.json();
  },

  // Regulation Library
  async getRegulations(status?: string): Promise<OrganizationRegulation[]> {
    const url = status ? `${API_BASE}/library?status=${status}` : `${API_BASE}/library`;
    const res = await fetch(url, { headers: getHeaders() });
    if (!res.ok) throw new Error('Failed to load regulations library');
    return res.json();
  },

  async getRegulationById(id: string): Promise<OrganizationRegulation> {
    const res = await fetch(`${API_BASE}/library/${id}`, { headers: getHeaders() });
    if (!res.ok) throw new Error('Failed to load regulation details');
    return res.json();
  },

  async createRegulation(data: Partial<OrganizationRegulation>): Promise<OrganizationRegulation> {
    const res = await fetch(`${API_BASE}/library`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to create regulation');
    return res.json();
  },

  async updateRegulation(id: string, data: Partial<OrganizationRegulation>): Promise<OrganizationRegulation> {
    const res = await fetch(`${API_BASE}/library/${id}`, {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to update regulation');
    return res.json();
  },

  // Compliance Requirements
  async getRequirements(filters?: {
    regulation_id?: string;
    compliance_status?: string;
    risk_level?: string;
    is_covered?: boolean;
  }): Promise<ComplianceRequirement[]> {
    const params = new URLSearchParams();
    if (filters?.regulation_id) params.append('regulation_id', filters.regulation_id);
    if (filters?.compliance_status) params.append('compliance_status', filters.compliance_status);
    if (filters?.risk_level) params.append('risk_level', filters.risk_level);
    if (filters?.is_covered !== undefined) params.append('is_covered', String(filters.is_covered));

    const url = params.toString() ? `${API_BASE}/requirements?${params.toString()}` : `${API_BASE}/requirements`;
    const res = await fetch(url, { headers: getHeaders() });
    if (!res.ok) throw new Error('Failed to load compliance requirements');
    return res.json();
  },

  async getRequirementById(id: string): Promise<ComplianceRequirement> {
    const res = await fetch(`${API_BASE}/requirements/${id}`, { headers: getHeaders() });
    if (!res.ok) throw new Error('Failed to load requirement details');
    return res.json();
  },

  async createRequirement(data: Partial<ComplianceRequirement> & { canonical_requirement_id?: string }): Promise<ComplianceRequirement> {
    const res = await fetch(`${API_BASE}/requirements`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to create compliance requirement');
    return res.json();
  },

  async updateRequirement(id: string, data: Partial<ComplianceRequirement>): Promise<ComplianceRequirement> {
    const res = await fetch(`${API_BASE}/requirements/${id}`, {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to update compliance requirement');
    return res.json();
  },

  // Source Links
  async addSourceLink(requirementId: string, canonicalRequirementId: string, notes?: string): Promise<ComplianceRequirementSourceLink> {
    const res = await fetch(`${API_BASE}/requirements/${requirementId}/source-links`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ canonical_requirement_id: canonicalRequirementId, notes }),
    });
    if (!res.ok) throw new Error('Failed to add source link');
    return res.json();
  },

  async removeSourceLink(requirementId: string, canonicalId: string): Promise<boolean> {
    const res = await fetch(`${API_BASE}/requirements/${requirementId}/source-links/${canonicalId}`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
    return res.ok;
  },

  // Assessments
  async getAssessments(requirementId: string): Promise<ComplianceRequirementAssessment[]> {
    const res = await fetch(`${API_BASE}/requirements/${requirementId}/assessments`, { headers: getHeaders() });
    if (!res.ok) throw new Error('Failed to load assessments');
    return res.json();
  },

  async recordAssessment(
    requirementId: string,
    data: {
      assessor_name: string;
      assessor_role: string;
      status_assigned: string;
      why_compliant?: string;
      gap_analysis?: string;
      evidence_summary?: string;
      finding_reference?: string;
      capa_reference?: string;
    }
  ): Promise<ComplianceRequirementAssessment> {
    const res = await fetch(`${API_BASE}/requirements/${requirementId}/assessments`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to record compliance assessment');
    return res.json();
  },

  // Operational Mappings
  async addMapping(
    requirementId: string,
    data: {
      mapping_type: string;
      target_reference_id: string;
      target_title: string;
      control_strength?: string;
      created_by: string;
    }
  ): Promise<ComplianceRequirementMapping> {
    const res = await fetch(`${API_BASE}/requirements/${requirementId}/mappings`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to add operational mapping');
    return res.json();
  },

  async removeMapping(mappingId: string): Promise<boolean> {
    const res = await fetch(`${API_BASE}/mappings/${mappingId}`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
    return res.ok;
  },

  // Regulatory Changes
  async getRegulatoryChanges(): Promise<RegulatoryChangeRecord[]> {
    const res = await fetch(`${API_BASE}/changes`, { headers: getHeaders() });
    if (!res.ok) throw new Error('Failed to load regulatory changes');
    return res.json();
  },

  async createRegulatoryChange(data: Partial<RegulatoryChangeRecord>): Promise<RegulatoryChangeRecord> {
    const res = await fetch(`${API_BASE}/changes`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to create regulatory change record');
    return res.json();
  },

  async transitionRegulatoryChange(
    id: string,
    newStatus: string,
    verificationNotes?: string,
    closedBy?: string
  ): Promise<RegulatoryChangeRecord> {
    const res = await fetch(`${API_BASE}/changes/${id}/transition`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ new_status: newStatus, verification_notes: verificationNotes, closed_by: closedBy }),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Failed to transition regulatory change');
    }
    return res.json();
  },

  async addChangeAction(
    changeId: string,
    data: {
      title: string;
      action_type: string;
      assigned_department: string;
      assignee: string;
      due_date: string;
    }
  ): Promise<RegulatoryChangeAction> {
    const res = await fetch(`${API_BASE}/changes/${changeId}/actions`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to create change action');
    return res.json();
  },

  async updateChangeAction(
    actionId: string,
    data: { status?: string; evidence_notes?: string }
  ): Promise<RegulatoryChangeAction> {
    const res = await fetch(`${API_BASE}/changes/actions/${actionId}`, {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to update change action');
    return res.json();
  },
};
